import z from "zod";

export {
  FEATURED_CONNECTOR_IDS as FIVETRAN_FEATURED_TYPES,
  IMPLEMENTED_CONNECTOR_IDS as FIVETRAN_SERVICE_TYPES,
  type ImplementedConnectorId as FivetranServiceType,
} from "./fivetran.connectors";

import { IMPLEMENTED_CONNECTOR_IDS } from "./fivetran.connectors";

export const FivetranServiceEnum = z.enum(IMPLEMENTED_CONNECTOR_IDS);

const connectionBaseSchema = z.object({
  service: FivetranServiceEnum,
  name: z.string().min(1, "Name is required"),
  datasourceId: z.string().min(1, "Destination is required"),
  destination_schema_names: z
    .enum(["FIVETRAN_NAMING", "SOURCE_NAMING"])
    .optional(),
});

/** Fivetran naming rule shared by schema and table names. */
const FIVETRAN_NAME_RE = /^[a-z_][a-z0-9_]*$/;

const schemaNameSchema = z
  .string()
  .min(1, "Schema is required")
  .regex(
    FIVETRAN_NAME_RE,
    "Schema can only contain lowercase letters, numbers and underscores, and cannot begin with a number",
  );

// Service schemas

export const attioSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.attio),
  config: z.object({
    schema: schemaNameSchema,
  }),
});

export const facebookAdsSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.facebook_ads),
  config: z.object({
    schema: schemaNameSchema,
  }),
});

export const googleSheetsSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.google_sheets),
  config: z.object({
    schema: schemaNameSchema,
    table: z
      .string()
      .min(1, "Table is required")
      .regex(
        FIVETRAN_NAME_RE,
        "Table can only contain lowercase letters, numbers and underscores, and cannot begin with a number",
      ),
    named_range: z.string().min(1, "Named range is required"),
    auth_type: z.enum(["ServiceAccount", "OAuth"]).default("ServiceAccount"),
    sheet_id: z.string().min(1, "Sheet ID is required"),
  }),
});

export const hubspotSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.hubspot),
  config: z.object({
    schema: schemaNameSchema,
    historical_sync_time_frame: z
      .enum(["ALL_TIME", "DATE_LIMIT"])
      .default("ALL_TIME"),
    // The Format should be YYYY-MM-DD
    backward_sync_limit_date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format")
      .optional(),
  }),
});

export const githubSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.github),
  config: z.object({
    schema: schemaNameSchema,
  }),
});

export const posthogSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.posthog),
  config: z.object({
    schema: schemaNameSchema,
    sub_domain: z.enum(["app", "eu"]),
    api_key: z.string().min(1, "API key is required"),
  }),
});

export const salesforceSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.salesforce),
  destination_schema_names:
    connectionBaseSchema.shape.destination_schema_names.default(
      "FIVETRAN_NAMING",
    ),
  config: z.object({
    schema: schemaNameSchema,
    sync_formula_fields: z.boolean().default(false),
  }),
});

export const stripeSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.stripe),
  config: z
    .object({
      schema: schemaNameSchema,
      api_key: z.string().min(1, "API key is required"),
      support_connected_accounts_sync: z.boolean(),
      historical_sync_time_frame: z.enum(["ALL_TIME", "DATE_LIMIT"]),
      backward_sync_limit: z.string(),
    })
    .superRefine((data, ctx) => {
      if (
        data.historical_sync_time_frame === "DATE_LIMIT" &&
        !data.backward_sync_limit
      ) {
        ctx.addIssue({
          code: "custom",
          message: "Backward sync limit is required when using DATE_LIMIT",
          path: ["backward_sync_limit"],
        });
      }
    }),
});

/**
 * Shopify authenticates via the Fivetran Connect Card (token, public-app OAuth,
 * or custom-app), so the only field we supply at create time is the destination
 * schema.
 */
export const shopifySchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.shopify),
  config: z.object({
    schema: schemaNameSchema,
  }),
});

/**
 * QuickBooks authenticates via the Fivetran Connect Card (Intuit OAuth 2.0), so
 * the only field we supply at create time is the destination schema.
 */
export const quickbooksSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.quickbooks),
  config: z.object({
    schema: schemaNameSchema,
  }),
});

/**
 * Amazon Selling Partner authenticates via the Fivetran Connect Card, where the
 * user supplies their SP-API app credentials and source settings, so the only
 * field we supply at create time is the destination schema.
 */
export const amazonSellingPartnerSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.amazon_selling_partner),
  config: z.object({
    schema: schemaNameSchema,
  }),
});

export const klaviyoSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.klaviyo),
  config: z
    .object({
      schema: schemaNameSchema,
      api_key: z.string().min(1, "API key is required"),
      historical_sync_limit: z
        .enum(["ALL_TIME", "DATE_LIMIT"])
        .default("ALL_TIME"),
      // The format should be YYYY-MM-DD
      historical_sync_limit_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format")
        .optional(),
    })
    .superRefine((data, ctx) => {
      if (
        data.historical_sync_limit === "DATE_LIMIT" &&
        !data.historical_sync_limit_date
      ) {
        ctx.addIssue({
          code: "custom",
          message: "Sync start date is required when using DATE_LIMIT",
          path: ["historical_sync_limit_date"],
        });
      }
    }),
});

export const tripleWhaleSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.triple_whale),
  config: z.object({
    schema: schemaNameSchema,
    api_key: z.string().min(1, "API key is required"),
    shop_domain: z.string().min(1, "Shop domain is required"),
  }),
});

export const fulfilSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.fulfil),
  config: z.object({
    schema: schemaNameSchema,
    api_key: z.string().min(1, "API key is required"),
    workspace_id: z.string().min(1, "Workspace ID is required"),
  }),
});

/**
 * NetSuite SuiteAnalytics token-based authentication. Credentials come from
 * NetSuite's "Set Up SuiteAnalytics Connect" page, an Integration record, and
 * an Access Token. `datasource` is fixed to `NetSuite2.com` for token auth and
 * is not surfaced in the UI.
 */
export const netsuiteSuiteanalyticsSchema = connectionBaseSchema.extend({
  service: z.literal(FivetranServiceEnum.enum.netsuite_suiteanalytics),
  config: z.object({
    schema: schemaNameSchema,
    host: z.string().min(1, "Service host is required"),
    port: z.coerce.number().int().positive("Service port is required"),
    account: z.string().min(1, "Account ID is required"),
    datasource: z.literal("NetSuite2.com").default("NetSuite2.com"),
    role: z.string().min(1, "Role ID is required"),
    consumer_key: z.string().min(1, "Consumer key is required"),
    consumer_secret: z.string().min(1, "Consumer secret is required"),
    token_key: z.string().min(1, "Token ID is required"),
    token_secret: z.string().min(1, "Token secret is required"),
  }),
});

export const FivetranConnectionSchema = z.discriminatedUnion("service", [
  attioSchema,
  facebookAdsSchema,
  githubSchema,
  googleSheetsSchema,
  hubspotSchema,
  posthogSchema,
  salesforceSchema,
  stripeSchema,
  shopifySchema,
  quickbooksSchema,
  amazonSellingPartnerSchema,
  klaviyoSchema,
  tripleWhaleSchema,
  fulfilSchema,
  netsuiteSuiteanalyticsSchema,
]);
